// Ask Tom — side-channel Q&A input.
//
// Renders below the main prompt textarea, above the upload-image button.
// Distinct from the generation prompt: submitting here calls the ask-tom
// edge function which answers with plain prose (no code, no artifact,
// no tools) using the conversation's current model + latest artifact
// code as context.
//
// Three states this component renders:
//   1. Hidden (Q&A hidden for this conversation):
//      Replaced by a single grey "Unhide question & answer chat." button.
//      Clicking it flips qaHidden -> false and the input returns.
//   2. Visible + idle: thin text input with a circle grey send button.
//   3. Visible + sending: input disabled, button shows a spinner.

import { useState } from 'react';
import { ArrowUp, Loader2 } from '@/components/ui/icons';
import { useAskTomMutation } from '@/services/messageService';
import { setQaHidden } from '@/lib/qaHiddenStore';
import type { Model } from '@shared/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AskTomInputProps {
  conversationId: string;
  model: Model;
  /** Toggled by the local state, but also persisted in qaHiddenStore so
   *  it survives reload. The parent (ChatSection) drives the same state
   *  for the message-list filter — single source of truth. */
  hidden: boolean;
  onHiddenChange: (hidden: boolean) => void;
}

export function AskTomInput({
  conversationId,
  model,
  hidden,
  onHiddenChange,
}: AskTomInputProps) {
  const [question, setQuestion] = useState('');
  const { mutateAsync: askTom, isPending } = useAskTomMutation({
    conversationId,
  });

  const handleSubmit = async () => {
    const q = question.trim();
    if (!q || isPending) return;
    setQuestion('');
    try {
      await askTom({ question: q, model });
    } catch (err) {
      // Mutation's onError already routes through Sentry; the assistant
      // edge function ALSO inserts a fallback "Tom couldn't reach..."
      // message into the chat, so the user sees the error there too.
      console.error('[AskTomInput] askTom failed', err);
    }
  };

  if (hidden) {
    return (
      <div className="flex w-full items-center justify-center border-t border-[#2a2a2a] p-2">
        <button
          type="button"
          onClick={() => {
            setQaHidden(conversationId, false);
            onHiddenChange(false);
          }}
          className="rounded-md border border-[#2a2a2a] bg-telekinesis-background-2 px-3 py-1.5 font-mono text-xs text-telekinesis-text-secondary transition-colors hover:border-telekinesis-neutral-500 hover:text-telekinesis-text-primary"
        >
          Unhide question &amp; answer chat.
        </button>
      </div>
    );
  }

  const isActive = !isPending && question.trim().length > 0;

  return (
    // No outer border/box around the field anymore — the Q&A row reads
    // as a continuation of the prompt input above, separated only by
    // the top border that divides input sections.
    <div className="flex w-full items-center gap-2 border-t border-[#2a2a2a] p-2">
      <input
        type="text"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        disabled={isPending}
        placeholder="Ask Tom a question:"
        // px-3 sm:px-4 mirrors the iterate Textarea's horizontal padding
        // (TextAreaChat.tsx:1621). Both rows already share the same p-2 row
        // container, so matching the inner padding makes the two placeholders
        // (and typed text) start at the same x — vertically aligned.
        className="flex-1 bg-transparent px-3 py-1.5 text-sm text-telekinesis-text-primary placeholder:text-telekinesis-text-secondary focus:outline-none disabled:opacity-50 sm:px-4"
      />
      {/* Send button. Active state (input has text + not loading):
          green glow on hover signals "ready to run 3D Analysis."
          Inactive state: dimmed, no glow, tooltip prompts the user to
          type something first. Tooltip text switches on the active
          flag so users learn the feature name ("3D Analysis"). */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending || !question.trim()}
            className={
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-telekinesis-neutral-700 text-white transition-all duration-200 ' +
              (isActive
                ? 'hover:scale-105 hover:bg-[#107d40] hover:shadow-[0_0_18px_rgba(16,125,64,0.6)]'
                : 'cursor-default opacity-50')
            }
            aria-label={
              isActive
                ? '3D Analysis'
                : 'Type a question to activate 3D Analysis'
            }
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-5 w-5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end">
          <span>
            {isActive
              ? '3D Analysis'
              : 'Type a question to activate 3D Analysis'}
          </span>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
